<template>
  <v-card flat>
    <v-list-item>
      <v-list-item-content two-line class="pt-0">
        <v-list-item-title class="headline">{{
          $t('personnePhysique.creation-masse.title')
        }}</v-list-item-title>
        <v-list-item-subtitle>
          Créer en masses des contacts à partir d'un fichier Excel au format
          CSV.
        </v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>
    <v-card-text>
      <v-stepper v-model="step" vertical>
        <v-stepper-step step="1" :complete="step1Ok">
          Uploader un fichier
        </v-stepper-step>

        <v-stepper-content step="1">
          <p>
            Le fichier doit être un fichier .csv, avec le caractère
            <code>;</code> comme séparateur et encodé en CP1252.
          </p>
          <p>
            Vous pouvez télécharger un
            <a @click="telechargeExemple"> fichier d'exemple </a>.
          </p>
          <v-file-input
            dense
            filled
            label="Fichier"
            :accept="['text/csv']"
            v-model="fichier"
            :disabled="uploadlingFichier"
          />
          <v-btn
            color="primary"
            @click="upload"
            :loading="uploadlingFichier"
            :disabled="!this.fichier"
          >
            Uploader le fichier
          </v-btn>
        </v-stepper-content>

        <v-stepper-step step="2" :complete="step2Ok">
          Vérifier les données
        </v-stepper-step>

        <v-stepper-content step="2">
          <p>Attention : les lignes en erreurs seront ignorées.</p>
          <p>
            Vous pouvez consulter et corriger le détail d'une ligne en cliquant
            sur le nom ou le prénom.
          </p>

          <v-data-table :headers="headersDonnees" :items="donnees" dense>
            <template #item.nom="{ index, item }">
              <a @click="affichePersonne(index, item)">
                {{ item.nom }}
              </a>
            </template>
            <template #item.prenoms="{index, item }">
              <a @click="affichePersonne(index, item)">
                {{ item.prenoms }}
              </a>
            </template>
            <template #item.action="{ item }">
              <template v-if="!!item.erreurs">
                <v-icon color="error">mdi-alert-circle</v-icon>
              </template>
            </template>
            <template #item.raisonSociale="{ item }">
              {{ item.contacts[0].personneMorale.raisonSociale }}
            </template>
          </v-data-table>
          <v-dialog v-model="modalPersonneVisible" persistent>
            <v-card>
              <v-card-title>Détail de la ligne</v-card-title>
              <v-card-text>
                <viewPersonnePhysique
                  v-if="modalPersonneVisible"
                  ref="view"
                  :enums="enums"
                  :form="personneVisible"
                  :control="control"
                  donneesConfidentiellesVisibles
                  enModeCreation
                >
                </viewPersonnePhysique>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn color="primary" @click="validerPersonneVisible">
                  Valider
                </v-btn>
                <v-btn @click="closeModalPersonneVisible" text>
                  {{ $t('button.cancel') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-btn
            color="primary"
            :disabled="!listePersonnesOk.length"
            @click="poursuivreVersCreation"
          >
            Poursuivre
          </v-btn>
          <v-btn text @click="annuler2"> Annuler </v-btn>
        </v-stepper-content>

        <v-stepper-step step="3" :complete="step3Ok">
          Confirmer la création des contacts
        </v-stepper-step>

        <v-stepper-content step="3">
          <p>
            Vous aller demander la création de
            {{ listePersonnesOk.length }} contact(s).
          </p>

          <v-form ref="commentaire">
            <v-textarea
              counter
              filled
              label="Commentaire"
              v-model="commentaire"
              :rules="[size255Rule()]"
              hide-details="auto"
            ></v-textarea>
          </v-form>

          <p>Voulez vous continuer ?</p>
          <v-btn color="primary" @click="demarrerCreation"> Poursuivre </v-btn>
          <v-btn text @click="annuler3"> Annuler </v-btn>
        </v-stepper-content>

        <v-stepper-step step="4" :complete="done">
          Créations des contacts</v-stepper-step
        >

        <v-stepper-content step="4">
          <template v-if="working">
            <p>
              Création des demandes, veuillez patientier.
            </p>
          </template>
          <template v-else>
            <p>
              Le processus est terminé. {{ nombreCree }} demande(s) ont été
              créé(s).
            </p>
          </template>
          <v-progress-circular
            :size="100"
            :width="15"
            :value="progressValue"
            color="primary"
          >
            {{ indexCreation }} / {{ listePersonnesOk.length }}
          </v-progress-circular>
        </v-stepper-content>
      </v-stepper>
    </v-card-text>
  </v-card>
</template>

<script>
import ViewPersonnePhysique from './ViewPersonnePhysique';
import ValidationRules from '@/plugins/mixins/ValidationRules';

function ouvreBlob(nom, timeout = 120000) {
  return response => {
    const blob = response.data;
    const file = new Blob([blob], { type: 'text/csv; charset=windows-1252' });
    var href = URL.createObjectURL(file);

    const link = window.document.createElement('a');
    link.href = href;
    link.setAttribute('download', nom);
    if (typeof link.download === 'undefined') {
      link.setAttribute('target', '_blank');
    }
    link.click();

    // Fixes "webkit blob resource error 1"
    setTimeout(() => {
      window.URL.revokeObjectURL(href);
    }, timeout);
  };
}

export default {
  components: {
    viewPersonnePhysique: ViewPersonnePhysique,
  },
  mixins: [ValidationRules],
  provide() {
    return {
      errors: this.errors,
    };
  },
  data() {
    return {
      enums: {
        civilites: [],
        fonctions: [],
        typesContact: [],
        typesAccesAgil: [],
        typesAcces: [],
        typesLait: [],
        personnesMorales: [],
        typeParticipant: [],
      },
      errors: {
        fields: {},
      },
      donnees: [],
      indexPersonneVisible: 0,
      personneVisible: null,
      modalPersonneVisible: false,
      validingPersonneVisible: false,
      commentaire: null,
      control: {},
      headersDonnees: [
        {
          text: '',
          value: 'action',
          sortable: false,
          width: '30px',
        },
        {
          text: 'Ligne',
          value: 'numero',
          sortable: false,
          width: '30px',
        },
        {
          text: 'Nom',
          value: 'nom',
          sortable: false,
        },
        {
          text: 'Prénom',
          value: 'prenoms',
          sortable: false,
        },
        {
          text: 'Raison sociale',
          value: 'raisonSociale',
          sortable: false,
        },
      ],
      fichier: null,
      uploadlingFichier: false,
      step: 1,
      working: false,
      done: false,
      nombreCree: 0,
      indexCreation: 0,
    };
  },
  computed: {
    step1Ok() {
      return !!this.donnees.length;
    },
    step2Ok() {
      return this.step > 2;
    },
    step3Ok() {
      return this.step > 3;
    },
    listePersonnesOk() {
      return this.donnees.filter(d => !d.erreurs);
    },
    progressValue() {
      const l = this.listePersonnesOk.length;
      if (!l) return 0;
      return parseInt((parseFloat(this.indexCreation) / parseFloat(l)) * 100.0);
    },
  },
  mounted() {
    this.loadEnums();
  },
  methods: {
    affichePersonne(index, item) {
      this.indexPersonneVisible = parseInt(item.numero - 1);
      this.personneVisible = JSON.parse(JSON.stringify(item));
      this.personneVisible.contacts[0].invalid = 0;

      this.chargeErreurs(this.personneVisible.erreurs);
      this.modalPersonneVisible = true;

      this.$nextTick(() => {
        this.validateForms();
      });
    },
    chargeErreurs(erreurs) {
      this.errors.fields = {};
      const fields = this.errors.fields;
      (erreurs || []).forEach(e => {
        if (!fields[e.field]) {
          fields[e.field] = [];
        }
        fields[e.field].push(e);
      });
    },
    validateForms() {
      const forms = this.$refs.view.getForms();
      return forms.map(f => f.validate()).every(v => !!v);
    },
    telechargeExemple() {
      this.$api('workflow/personne-physique/import/exemple.csv')
        .get({
          responseType: 'blob',
        })
        .then(ouvreBlob('import_contact.csv'));
    },
    demarrerCreation() {
      if (!this.$refs.commentaire.validate()) {
        return;
      }

      this.nombreCree = 0;
      this.indexCreation = 0;
      this.working = false;
      this.step = 4;
      this.processNextCreation();
    },
    processNextCreation() {
      if (this.indexCreation >= this.listePersonnesOk.length) {
        this.working = false;
        this.done = false;
        return;
      }

      const personne = this.listePersonnesOk[this.indexCreation];
      const contact = { ...personne };
      contact.idDemandePersonneMorale =
        personne.contacts[0].demandePersonneMorale;
      contact.commentaire = this.commentaire;
      this.$api('workflow/personne-physique')
        .post(contact)
        .then(() => {
          this.nombreCree++;
        })
        .catch(() => {
          //TODO
        })
        .finally(() => {
          this.indexCreation++;
          this.processNextCreation();
        });
    },
    validerPersonneVisible() {
      this.chargeErreurs();
      const ok = this.validateForms();
      if (!ok) {
        return;
      }

      this.validingPersonneVisible = true;
      this.$api('workflow/personne-physique/import/valide')
        .post(this.personneVisible)
        .then(() => {
          const p = this.donnees[this.indexPersonneVisible];
          this.donnees.splice(this.indexPersonneVisible, 1, {
            numero: p.numero,
            ...this.personneVisible,
            erreurs: null,
          });
          this.closeModalPersonneVisible();
        })
        .catch(errors => {
          this.chargeErreurs(errors.fieldErrors);
          this.validateForms();
        })
        .finally(() => {
          this.validingPersonneVisible = false;
        });
    },
    closeModalPersonneVisible() {
      this.modalPersonneVisible = false;
      this.personneVisible = null;
    },
    poursuivreVersCreation() {
      this.step = 3;
      this.nombreCree = 0;
    },
    loadEnums() {
      this.$api('enums/personnephysique')
        .get()
        .then(({ data }) => {
          const enums = this.enums;
          enums.civilites = data.civilites;
          enums.fonctions = data.fonctions;
          enums.typesContact = data.typesContact;
          enums.typesAccesAgil = data.typesAccesAgil;
          enums.typesLait = data.typesLait;
          enums.typesAcces = data.typesAcces;
          enums.typeParticipant = data.typesParticipant;
        });
    },
    upload() {
      this.donnees = [];
      this.uploadlingFichier = true;
      const form = new FormData();
      form.append('fichier', this.fichier);
      this.$api('workflow/personne-physique/import/prepare')
        .post(form)
        .then(({ data }) => {
          this.donnees = data.map(d => ({
            ...d.personne,
            erreurs: d.erreurs,
            numero: d.numero,
          }));
          this.step = 2;
        })
        .finally(() => {
          this.uploadlingFichier = false;
        });
    },
    annuler2() {
      this.fichier = null;
      this.step = 1;
    },
    annuler3() {
      this.step = 2;
    },
  },
};
</script>
